// src/components/Sidebar.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';

// FIREBASE
import { db, auth } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';

// CONTEXT
import { AuthContext } from '../AuthContext';

// COMPONENTS
import CloseSidebar from './CloseSidebar';

// IMAGES
import comment_icon        from '../assets/images/icons/comment-icon.png';
import help_icon           from '../assets/images/icons/help-icon.png';
import dashboard_icon      from '../assets/images/icons/dashboard-icon.png';
import setting_icon        from '../assets/images/icons/setting-icon.png';
import youtube_white_logo  from '../assets/images/logo/youtube-white-logo.png';

/**
 * Compute the “Resets in …” label from a Date.
 * Returns null if no date, otherwise one of "4 weeks", "3 weeks", "... days", or "1 day".
 */
function computeTimeLabel(quotaCycleEnd) {
  if (!quotaCycleEnd) return null;

  const now = new Date();
  let days = Math.ceil((quotaCycleEnd - now) / (1000 * 60 * 60 * 24));
  if (days < 0) days = 0;

  if (days >= 28) {
    return '4 weeks';
  } else if (days >= 7) {
    const weeks = Math.ceil(days / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  } else if (days > 1) {
    return `${days} days`;
  } else {
    return '1 day';
  }
}

function Sidebar() {
  const location = useLocation();
  const currentView = location.pathname;
  const { currentUser } = useContext(AuthContext);
  const user = currentUser || auth.currentUser;
  const userId = user?.uid;

  const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 1280);

  // seed everything from localStorage (or fallbacks)
  const initialPlan = localStorage.getItem(`currentPlan_${userId}`) || 'Free Plan';
  const initialCreditsUsed = (() => {
    const stored = localStorage.getItem(`creditsUsed_${userId}`);
    return stored !== null ? Number(stored) : null;
  })();
  const initialQuotaCycleEnd = (() => {
    const stored = localStorage.getItem(`quotaCycleEnd_${userId}`);
    return stored ? new Date(stored) : null;
  })();
  const initialCreditsLimit = (() => {
    const stored = localStorage.getItem(`creditsLimit_${userId}`);
    return stored !== null ? Number(stored) : null;
  })();

  const [currentPlan, setCurrentPlan]     = useState(initialPlan);
  const [creditsUsed, setCreditsUsed]     = useState(initialCreditsUsed);
  const [quotaCycleEnd, setQuotaCycleEnd] = useState(initialQuotaCycleEnd);
  const [creditsLimit, setCreditsLimit]   = useState(initialCreditsLimit);
  // initialize timeLabel immediately to avoid initial “--” flicker
  const [timeLabel, setTimeLabel]         = useState(() =>
    computeTimeLabel(initialQuotaCycleEnd)
  );

  // handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsNarrowScreen(window.innerWidth < 1280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // fetch & subscribe planDetails
  useEffect(() => {
    if (!user) return;

    const planDetailsRef = doc(db, 'users', userId, 'subscriptionData', 'planDetails');

    // initial fetch
    getDoc(planDetailsRef)
      .then(snap => {
        if (!snap.exists()) return;
        const data = snap.data();

        // plan
        const plan = data.currentPlan || 'Free Plan';
        setCurrentPlan(plan);
        localStorage.setItem(`currentPlan_${userId}`, plan);

        // credits used
        if (data.creditsUsed != null) {
          setCreditsUsed(data.creditsUsed);
          localStorage.setItem(`creditsUsed_${userId}`, data.creditsUsed.toString());
        }

        // credits limit
        if (data.creditsLimit != null) {
          setCreditsLimit(data.creditsLimit);
          localStorage.setItem(`creditsLimit_${userId}`, data.creditsLimit.toString());
        }

        // quota cycle end
        if (data.quotaCycleEnd?.toDate) {
          const endDate = data.quotaCycleEnd.toDate();
          setQuotaCycleEnd(endDate);
          localStorage.setItem(`quotaCycleEnd_${userId}`, endDate.toISOString());
        }
      })
      .catch(() => {
        // leave state as-is on error
      });

    // realtime updates
    const unsubscribePlan = onSnapshot(planDetailsRef, snap => {
      if (!snap.exists()) return;
      const data = snap.data();

      // plan
      const plan = data.currentPlan || 'Free Plan';
      if (localStorage.getItem(`currentPlan_${userId}`) !== plan) {
        setCurrentPlan(plan);
        localStorage.setItem(`currentPlan_${userId}`, plan);
      }

      // credits used
      if (data.creditsUsed != null) {
        setCreditsUsed(data.creditsUsed);
        localStorage.setItem(`creditsUsed_${userId}`, data.creditsUsed.toString());
      }

      // credits limit
      if (data.creditsLimit != null) {
        setCreditsLimit(data.creditsLimit);
        localStorage.setItem(`creditsLimit_${userId}`, data.creditsLimit.toString());
      }

      // quota cycle end
      if (data.quotaCycleEnd?.toDate) {
        const endDate = data.quotaCycleEnd.toDate();
        setQuotaCycleEnd(endDate);
        localStorage.setItem(`quotaCycleEnd_${userId}`, endDate.toISOString());
      }
    });

    return () => unsubscribePlan();
  }, [user, userId]);

  // update timeLabel whenever quotaCycleEnd changes
  useEffect(() => {
    setTimeLabel(computeTimeLabel(quotaCycleEnd));
  }, [quotaCycleEnd]);

  if (isNarrowScreen) {
    return <CloseSidebar />;
  }

  // nav helpers
  const isBumperOrLinks = () =>
    currentView.startsWith('/bumper') ||
    currentView.startsWith('/links')  ||
    currentView.startsWith('/video');

  const isCurrentView = view => currentView.startsWith(view);

  const getLinkClass = view =>
    view === '/bumper'
      ? `sidebar-link ${isBumperOrLinks() ? 'active' : ''}`
      : `sidebar-link ${isCurrentView(view) ? 'active' : ''}`;

  const getIconClass = view =>
    view === '/bumper'
      ? `link-icon ${isBumperOrLinks() ? 'active-icon' : ''}`
      : `link-icon ${isCurrentView(view) ? 'active-icon' : ''}`;

  // usage bar: compute remaining and fill percentage
  const maxCredits = creditsLimit != null ? creditsLimit : null;
  const remaining = (maxCredits != null && creditsUsed != null)
    ? Math.max(maxCredits - creditsUsed, 0)
    : null;
  const fillPct = (remaining != null && maxCredits != null)
    ? Math.min((remaining / maxCredits) * 100, 100)
    : 0;

  return (
    <div className="sidebar">
      <Link to="/dashboard" className={getLinkClass('/dashboard')}>
        <img src={dashboard_icon} alt="Dashboard" className={getIconClass('/dashboard')} />
        <span className="sidebar-text">Dashboard</span>
      </Link>

      <div className="dividersides" />

      <Link to="/workspace" className={getLinkClass('/workspace')}>
        <img src={comment_icon} alt="Chat" className={getIconClass('/workspace')} />
        <span className="sidebar-text">Chat Workspace</span>
      </Link>

      <Link to="/bumper" className={getLinkClass('/bumper')}>
        <img src={youtube_white_logo} alt="Creator Studio" className={getIconClass('/bumper')} />
        <span className="sidebar-text">Creator Studio</span>
      </Link>

      <a
        href="https://intercom.help/bumpups/en/"
        target="_blank"
        rel="noopener noreferrer"
        className={getLinkClass('/help')}
      >
        <img src={help_icon} alt="Help" className={getIconClass('/help')} />
        <span className="sidebar-text">Help</span>
      </a>

      <div className="plan-section">
        {/* Usage Bar */}
        <div className="usage-bar">
          <div className="usage-bar-header">
            <span>Included minutes</span>
            <span>
              {remaining != null ? remaining : '--'} / {maxCredits != null ? maxCredits : '--'}
            </span>
          </div>
          <div className="usage-bar-track">
            <div
              className="usage-bar-fill"
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <div className="usage-bar-reset">
            Usage resets in {timeLabel !== null ? timeLabel : '--'}
          </div>
        </div>

        <div className="sidebar-footer-details">
          <Link to="/settings/plan" className="plan-link">
            {currentPlan.toLowerCase() === 'free'
              ? 'Free Plan'
              : currentPlan.toLowerCase() === 'pro'
                ? 'Pro Plan'
                : currentPlan}
          </Link>
          <Link to="/settings" className="side-settings-button">
            <img src={setting_icon} alt="Settings" className="side-settings-icon" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
